import { declareClient } from '../react-query/declare-client.js';
import type { MockBuilderInstance } from './mock-builder.js';

/**
 * Pairs with `mockBuilder()` to produce a react-query client.
 * Semantically identical to `declareClient({ api: realBuilder })` — handlers
 * returned by `mockBuilder` match `BuilderInstance`'s surface. The `mock`
 * field on each endpoint config is forwarded through to `api.declareEndpoint`
 * via the pass-through in `declareClient` (see MINT-OVERRIDE there).
 *
 * `mock` is declared as an optional field on `BaseEndpointOptions` (see
 * MINT-OVERRIDE in `types/config.ts`), so callers get IntelliSense without
 * `@ts-expect-error`. `mockBuilder` enforces presence at runtime — calling
 * an endpoint without a mock throws immediately, on the first invocation.
 *
 * @example
 * ```ts
 * const api = mockBuilder({})
 * const client = mockDeclareClient({ api })
 * const getUser = client.query({
 *   method: 'GET',
 *   url: '/users/$id',
 *   responseSchema: userSchema,
 *   mock: ({ urlParams }) => ({ id: String(urlParams?.id), name: 'Mock' }),
 * })
 * ```
 */
export function mockDeclareClient<
  UseDiscriminator extends boolean = false,
>(options: {
  api: MockBuilderInstance<UseDiscriminator>;
  defaults?: Parameters<typeof declareClient<UseDiscriminator>>[0]['defaults'];
}) {
  return declareClient<UseDiscriminator>({
    api: options.api as never,
    defaults: options.defaults,
  });
}
